import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Form, Formik } from "formik";
import MaterialToolTip from "components/MaterialToolTip";
import SignInLogo from "assets/svg/SignInLogo";
import Button from "components/Button";
import { stateConvert } from "utils/stateAbbreviation";
import LeftContainer from "components/LeftContainer";
import NameInput from "components/FormElements/NameInput";
import TextInput from "components/FormElements/TextInput";
import CheckboxInput from "components/FormElements/CheckboxInput";
import EmailFieldInput from "components/FormElements/EmailFieldInput";
import PasswordErrorMsg from "components/PasswordErrorMsg";
import PasswordIconText from "assets/svg/PasswordIconText";
import EndIcon from "assets/svg/PasswordShowIcon";
import PhoneNumber from "components/FormElements/PhoneNumber";
import NumberInput from "components/FormElements/NumberInput";
import Select from "components/FormElements/Select";
import PatternFormat from "components/FormElements/PatternFormatInput";
import VerifyOTP from "components/VerifyOTP";
import { emailOtp } from "redux/actions/otpVerification";
import { emailValidateApi, zipCodeValidate } from "redux/actions/registerUser";
import AlertModal from "components/AlertModal";
import { getAuthInitialValues, getValidateSchema } from "utils/signUpUtils";
import { useText } from "locales/useGetText";
import {
  BodyContainer,
  CreateAccountContainer,
  InputField,
  CheckBoxInputField,
  FormBody,
  Footer,
  Container,
  RightContainer,
  LogoWrapper,
  CreateAccountFormWrapper,
} from "styles/pages/global";
import { getCanadaStateAbbrByName } from "helpers";

const SignUp = () => {
  const locale = useText("signup");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  let formObj = {
    email: params.get("emailId"),
  };

  const [emailVerifyState, setEmailVerifyState] = useState(false);
  const [disableFlag, setDisableFlag] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [modal, setModal] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [modalLink, setModalLink] = useState("/");
  const toggleModal = () => setModal(!modal);

  const countryList = [
    { label: "USA", value: "USA" },
    { label: "Canada", value: "CANADA" },
  ];

  const [emailError, setEmailError] = useState("");
  const [emailBillingError, setEmailBillingError] = useState("");
  const emailValidate = async (email, setFieldError, setFieldTouched, type) => {
    const response = await dispatch(emailValidateApi(email));
    if (response?.data?.success) {
      if (type !== "email") {
        setEmailBillingError("");
      } else {
        setEmailError("");
      }
    } else {
      if (type !== "email") {
        setEmailBillingError(response?.data?.error?.message);
      } else {
        setEmailError(response?.data?.error?.message);
      }
    }
  };

  const [zipError, setZipError] = useState("");
  const [billZipError, setBillZipError] = useState("");
  const zipCodeValidator = async (props, zipCode, name) => {
    const { setFieldValue, setFieldError, values } = props;
    const country = values?.country;
    if (
      (country === "USA" && zipCode?.length === 5) ||
      (country === "CANADA" && zipCode?.length > 5)
    ) {
      const response = await dispatch(zipCodeValidate(zipCode, country));
      if (response?.data?.success) {
        let stateAbb = response?.data?.data?.state;
        if (country === "USA") stateAbb = stateConvert(response?.data?.data?.state);
        else if (country === "CANADA") stateAbb = getCanadaStateAbbrByName(response?.data?.data?.state);
        if (name === "billZip") {
          setBillZipError("");
          setFieldValue("billState", stateAbb);
          setFieldValue("billCity", response?.data?.data?.city);
        } else {
          setZipError("");
          setFieldValue("state", stateAbb);
          setFieldValue("city", response?.data?.data?.city);
        }
      } else {
        const errorMsg =
          country === "CANADA"
            ? locale("invalidPostalCode")
            : locale("invalidZipCode");
        if (name === "billZip") {
          setBillZipError(errorMsg);
        } else {
          setZipError(errorMsg);
        }
        setFieldError(name, errorMsg);
      }
    }
  };

  const formSubmit = async (props) => {
    const { submitForm, validateForm, setTouched, isValid } = props;
    validateForm();
    setTouched({});
    submitForm().then(async () => {
      if (isValid) {
        let data = {
          email: props?.values?.email,
          name: props?.values?.name,
        };
        const response = await dispatch(emailOtp(data));
        if (response?.data?.success) {
          setEmailVerifyState(true);
        } else {
          setModalContent(response?.data?.error?.message);
          setModalLink("");
          toggleModal();
        }
        window.scrollTo(0, 0);
        validateForm();
        setTouched({});
      }
    });
  };

  const billToFields = (props, setFieldValue, flag) => {
    const { validateForm, setTouched } = props;
    if (flag) {
      setFieldValue("billName", props?.values?.name);
      setFieldValue("billPhoneNumber", props?.values?.phoneNumber);
      setFieldValue("billContactName", props?.values?.businessName);

      setFieldValue("billAddress", props?.values?.address);
      setFieldValue("billCity", props?.values?.city);
      setFieldValue("billState", props?.values?.state);
      setFieldValue("billZip", props?.values?.zip);
      setDisableFlag(true);
    } else {
      setDisableFlag(false);
      setFieldValue("billName", "");
      setFieldValue("billPhoneNumber", "");
      setFieldValue("billContactName", "");

      setFieldValue("billAddress", "");
      setFieldValue("billCity", "");
      setFieldValue("billState", "");
      setFieldValue("billZip", "");
    }

    validateForm();
    setTouched({});
  };

  const [allErrors, setAllErrors] = useState([]);
  const [passwordVal, setPasswordVal] = useState([]);

  const checkForAllErrors = async (value) => {
    try {
      // Pass locale parameter here
      await getValidateSchema(disableFlag, locale).validate(
        {
          password: value,
        },
        { abortEarly: false }
      );
      setAllErrors([]);
    } catch (ex) {
      setAllErrors(ex.errors);
    }
  };

  const formFields = () => {
    return (
      <Container>
        <LeftContainer />
        <RightContainer className="for-create-account">
          <CreateAccountContainer>
            <LogoWrapper className="for-create-account">
              <SignInLogo />
            </LogoWrapper>
            {!emailVerifyState && (
              <p style={{ "text-align": "center", "margin-bottom": "20px" }}>
                {locale("registrationFormIntro")}
              </p>
            )}
            <CreateAccountFormWrapper>
              <FormBody className="create-account-form">
                <Formik
                  enableReinitialize={true}
                  initialValues={getAuthInitialValues(formObj)}
                  validationSchema={getValidateSchema(disableFlag, locale)}
                  onSubmit={() => { }}
                >
                  {(props) => {
                    const {
                      isValid,
                      dirty,
                      setFieldValue,
                      setFieldError,
                      setFieldTouched,
                    } = props;
                    return (
                      <>
                        {!emailVerifyState ? (
                          <>
                            <div
                              className="header-heading"
                              style={{ "margin-bottom": "20px" }}
                            >
                              <div style={{ "margin-top": "-5px" }}>
                                <h5>{locale("newAccountSetup")}</h5>
                              </div>
                              <div style={{ "margin-top": "-10px" }}>
                                <h6>
                                  {locale("alreadyHaveAccount")}
                                  <span onClick={() => navigate("/")}>
                                    {" "}
                                    {locale("logIn")}
                                  </span>
                                </h6>
                              </div>
                            </div>
                            <Form>
                              <div className="input-wrapper">
                                <div className="w-100">
                                  <InputField>
                                    <NameInput
                                      name="name"
                                      placeholder={locale("contactFullName")}
                                      maxLength={50}
                                    />
                                  </InputField>
                                  <InputField>
                                    <EmailFieldInput
                                      name="email"
                                      placeholder={locale("email")}
                                      maxLength={100}
                                      disabled={
                                        params.get("emailId") ? true : false
                                      }
                                      showErrorMessages={true}
                                      calllBack={(val, action) => {
                                        if (action === "keyDown") {
                                          setEmailError("");
                                        } else {
                                          if (val)
                                            emailValidate(
                                              val,
                                              setFieldError,
                                              setFieldTouched,
                                              "email"
                                            );
                                        }
                                      }}
                                    />
                                    {emailError && (
                                      <div className="app-input-text">
                                        <p
                                          name={"emailError"}
                                          className="error-message"
                                        >
                                          {emailError}
                                        </p>
                                      </div>
                                    )}
                                  </InputField>
                                  <InputField>
                                    <TextInput
                                      name="password"
                                      type={showPassword ? "text" : "password"}
                                      inputType="password"
                                      maxLength="20"
                                      placeholder={locale(
                                        "passwordPlaceholder"
                                      )}
                                      onChangeHandler={(val) => {
                                        setPasswordVal(val);
                                        checkForAllErrors(val);
                                      }}
                                    />
                                    {!showPassword ? (
                                      <EndIcon
                                        className="passwordIcon"
                                        onClick={() => {
                                          setShowPassword(!showPassword);
                                        }}
                                      />
                                    ) : (
                                      <PasswordIconText
                                        className="passwordIcon"
                                        onClick={() => {
                                          setShowPassword(!showPassword);
                                        }}
                                      />
                                    )}
                                  </InputField>
                                  <InputField>
                                    <TextInput
                                      name="confirmPassword"
                                      maxLength="20"
                                      type={
                                        showConfirmPassword
                                          ? "text"
                                          : "password"
                                      }
                                      placeholder={locale("confirmPassword")}
                                    />
                                    {!showConfirmPassword ? (
                                      <EndIcon
                                        className="passwordIcon"
                                        onClick={() => {
                                          setShowConfirmPassword(
                                            !showConfirmPassword
                                          );
                                        }}
                                      />
                                    ) : (
                                      <PasswordIconText
                                        className="passwordIcon"
                                        onClick={() => {
                                          setShowConfirmPassword(
                                            !showConfirmPassword
                                          );
                                        }}
                                      />
                                    )}
                                  </InputField>
                                </div>
                                <InputField className="error-msg-window">
                                  <PasswordErrorMsg
                                    error={allErrors}
                                    value={passwordVal}
                                    locale={locale}
                                  />
                                </InputField>{" "}
                              </div>
                              <h5 className="input-wrapper-heading">
                                {locale("companyProfile")}
                                <span className="star">*</span>
                              </h5>
                              <div className="input-wrapper">
                                <InputField>
                                  <TextInput
                                    name="businessName"
                                    inputType="CompanyName"
                                    maxLength={50}
                                    placeholder={locale("companyName")}
                                    onChangeHandler={(val) => {
                                      if (props?.values?.customerAddressCheck)
                                        setFieldValue("billContactName", val);
                                    }}
                                  />
                                </InputField>
                                <InputField>
                                  <TextInput
                                    name="address"
                                    maxLength={50}
                                    inputType="address"
                                    placeholder={locale("address")}
                                    onChangeHandler={(val) => {
                                      if (props?.values?.customerAddressCheck)
                                        setFieldValue("billAddress", val);
                                    }}
                                  />
                                </InputField>
                              </div>

                              <div className="input-wrapper">
                                <InputField>
                                  <NameInput
                                    name="city"
                                    placeholder={locale("city")}
                                    maxLength={50}
                                    disabled={
                                      props?.values?.country === "CANADA"
                                    }
                                    onChangeHandler={(val) => {
                                      if (props?.values?.customerAddressCheck)
                                        setFieldValue("billCity", val);
                                    }}
                                  />
                                </InputField>
                                <InputField>
                                  <NameInput
                                    name="state"
                                    placeholder={
                                      props?.values?.country === "CANADA"
                                        ? locale("territoryProvince")
                                        : locale("state")
                                    }
                                    maxLength={50}
                                    disabled={true}
                                  />
                                </InputField>
                              </div>

                              <div className="input-wrapper">
                                <InputField>
                                  <TextInput
                                    name="zip"
                                    inputType="returnText"
                                    maxLength={7}
                                    placeholder={
                                      props?.values?.country === "CANADA"
                                        ? locale("postalCode")
                                        : locale("zip")
                                    }
                                    disabled={!props.values?.country}
                                    onChangeHandler={(val) => {
                                      if (val) {
                                        zipCodeValidator(props, val, "zip");
                                        if (props?.values?.customerAddressCheck)
                                          setFieldValue("billZip", val);
                                      }
                                    }}
                                  />
                                  {zipError && (
                                    <div className="app-input-text">
                                      <p
                                        name={"zipError"}
                                        className="error-message"
                                      >
                                        {zipError}
                                      </p>
                                    </div>
                                  )}
                                </InputField>
                                <InputField>
                                  <Select
                                    name="country"
                                    placeholder={locale("country")}
                                    data={countryList}
                                    disabled={disableFlag}
                                    onChangeHandler={() => {
                                      setFieldValue("city", "");
                                      setFieldValue("state", "");
                                      setFieldValue("zip", "");
                                      setFieldValue("billCity", "");
                                      setFieldValue("billState", "");
                                      setFieldValue("billZip", "");
                                      setZipError("");
                                      setBillZipError("");
                                    }}
                                  />
                                </InputField>
                              </div>

                              <div className="input-wrapper">
                                <InputField>
                                  <PhoneNumber
                                    name="phoneNumber"
                                    placeholder={locale("companyPhone")}
                                    onChangeHandler={(val) => {
                                      if (props?.values?.customerAddressCheck)
                                        setFieldValue("billPhoneNumber", val);
                                    }}
                                  />
                                </InputField>
                                {props?.values?.country === "CANADA" ?
                                  <InputField>
                                    <TextInput
                                      name="taxId"
                                      maxLength={20}
                                      inputType=""
                                      placeholder={locale("registration_number")}
                                    />
                                  </InputField>
                                  :
                                  <InputField>
                                    <NumberInput
                                      name="taxId"
                                      decimalSeparator={false}
                                      maxLength={9}
                                      placeholder={locale("taxId")}
                                    />
                                  </InputField>
                                }
                              </div>

                              <div className="input-wrapper ">
                                <InputField>
                                  <PatternFormat
                                    name="dnbNum"
                                    format="##-###-####"
                                    placeholder={locale("dnsDuns")}
                                  />
                                </InputField>
                                <InputField>
                                  <TextInput
                                    name="businessUrl"
                                    maxLength={200}
                                    inputType=""
                                    placeholder={locale("businessUrl")}
                                  />
                                </InputField>
                              </div>

                              <div className="input-wrapper full-width">
                                <InputField>
                                  <TextInput
                                    name="businessDesc"
                                    maxLength={200}
                                    placeholder={locale("businessDescription")}
                                  />
                                </InputField>
                                <InputField></InputField>
                              </div>

                              <h5
                                className="input-wrapper-heading"
                                style={{ display: "flex" }}
                              >
                                {locale("billToInfo")}{" "}
                                <div
                                  style={{
                                    "margin-top": "-6px",
                                    "margin-left": "5px",
                                  }}
                                >
                                  <MaterialToolTip
                                    title={locale("billToTooltip")}
                                    class="passwordIcon"
                                  />
                                </div>
                                <span className="star">*</span>
                              </h5>

                              <div className="input-wrapper-heading">
                                <CheckBoxInputField>
                                  <CheckboxInput
                                    name="customerAddressCheck"
                                    label={locale("sameAsCompany")}
                                    onChange={(val) => {
                                      billToFields(props, setFieldValue, val);
                                      setFieldValue(
                                        "customerAddressCheck",
                                        val
                                      );
                                    }}
                                  />
                                </CheckBoxInputField>
                              </div>

                              <div
                                className={
                                  !disableFlag ? "displayShow" : "displayHide"
                                }
                              >
                                <div className="input-wrapper">
                                  <InputField>
                                    <NameInput
                                      name="billName"
                                      placeholder={locale("billingContactName")}
                                      maxLength={50}
                                      disabled={disableFlag}
                                    />
                                  </InputField>
                                  <InputField>
                                    <PhoneNumber
                                      name="billPhoneNumber"
                                      placeholder={locale(
                                        "billingContactPhone"
                                      )}
                                      disabled={disableFlag}
                                    />
                                  </InputField>
                                </div>
                                <div className="input-wrapper">
                                  <InputField>
                                    <TextInput
                                      name="billContactName"
                                      inputType="CompanyName"
                                      placeholder={locale("companyName")}
                                      maxLength={50}
                                      disabled={disableFlag}
                                    />
                                  </InputField>

                                  <InputField>
                                    <TextInput
                                      name="billAddress"
                                      placeholder={locale("address")}
                                      inputType="address"
                                      maxLength={50}
                                      disabled={disableFlag}
                                    />
                                  </InputField>
                                </div>
                                <div className="input-wrapper">
                                  <InputField>
                                    <NameInput
                                      name="billCity"
                                      placeholder={locale("city")}
                                      maxLength={50}
                                      disabled={
                                        disableFlag ||
                                        props?.values?.country === "CANADA"
                                      }
                                    />
                                  </InputField>
                                  <InputField>
                                    <NameInput
                                      name="billState"
                                      placeholder={
                                        props?.values?.country === "CANADA"
                                          ? locale("territoryProvince")
                                          : locale("state")
                                      }
                                      maxLength={50}
                                      disabled={true}
                                    />
                                  </InputField>
                                </div>

                                <div className="input-wrapper">
                                  <InputField>
                                    <TextInput
                                      name="billZip"
                                      inputType="returnText"
                                      maxLength={7}
                                      placeholder={
                                        props?.values?.country === "CANADA"
                                          ? locale("postalCode")
                                          : locale("zip")
                                      }
                                      disabled={!props.values?.country}
                                      onChangeHandler={(val) => {
                                        if (val) {
                                          zipCodeValidator(
                                            props,
                                            val,
                                            "billZip"
                                          );
                                        }
                                      }}
                                    />
                                    {billZipError && (
                                      <div className="app-input-text">
                                        <p
                                          name={"zipError"}
                                          className="error-message"
                                        >
                                          {billZipError}
                                        </p>
                                      </div>
                                    )}
                                  </InputField>
                                  <InputField>
                                    <Select
                                      name="country"
                                      placeholder={locale("country")}
                                      data={countryList}
                                      disabled={disableFlag}
                                      onChangeHandler={() => {
                                        setFieldValue("city", "");
                                        setFieldValue("state", "");
                                        setFieldValue("zip", "");
                                        setFieldValue("billCity", "");
                                        setFieldValue("billState", "");
                                        setFieldValue("billZip", "");
                                        setBillZipError("");
                                        setZipError("");
                                      }}
                                    />
                                  </InputField>
                                </div>
                              </div>

                              <div className="input-wrapper">
                                <InputField>
                                  <EmailFieldInput
                                    name="billingContactEmail"
                                    placeholder={locale("billingContactEmail")}
                                    maxLength={45}
                                    showErrorMessages={true}
                                    calllBack={(val, action) => {
                                      if (action === "keyDown") {
                                        setEmailBillingError("");
                                      } else {
                                        if (val)
                                          emailValidate(
                                            val,
                                            setFieldError,
                                            setFieldTouched,
                                            "billingContactEmail"
                                          );
                                      }
                                    }}
                                  />

                                  <MaterialToolTip
                                    title={locale("billingEmailTooltip")}
                                    class="passwordIcon"
                                  />
                                  {emailBillingError && (
                                    <div className="app-input-text">
                                      <p
                                        name={"emailError"}
                                        className="error-message"
                                      >
                                        {emailBillingError}
                                      </p>
                                    </div>
                                  )}
                                </InputField>

                                <InputField>
                                  <EmailFieldInput
                                    name="otherContactEmail"
                                    placeholder={locale("otherContactEmail")}
                                    maxLength={45}
                                    showErrorMessages={true}
                                    calllBack={(val, action) => {
                                      if (action === "keyDown") {
                                        setEmailBillingError("");
                                      } else {
                                        if (val)
                                          emailValidate(
                                            val,
                                            setFieldError,
                                            setFieldTouched,
                                            "otherContactEmail"
                                          );
                                      }
                                    }}
                                  />
                                  {emailBillingError && (
                                    <div className="app-input-text">
                                      <p
                                        name={"emailError"}
                                        className="error-message"
                                      >
                                        {emailBillingError}
                                      </p>
                                    </div>
                                  )}
                                </InputField>
                              </div>
                              <div className="input-wrapper">
                                <CheckboxInput
                                  name="termsAndCondition"
                                  label={locale("termsAndConditions")}
                                  onChange={(val) => {
                                    setFieldValue("termsAndCondition", val);
                                  }}
                                />
                              </div>
                              <Footer>
                                <div
                                  style={{ width: "150px", margin: "0 auto" }}
                                >
                                  <Button
                                    disabled={
                                      !(isValid && dirty && !emailBillingError)
                                    }
                                    handleClick={() => formSubmit(props)}
                                  >
                                    {locale("createAccount")}
                                  </Button>
                                </div>
                              </Footer>
                            </Form>
                          </>
                        ) : (
                          <VerifyOTP props={props} />
                        )}
                      </>
                    );
                  }}
                </Formik>
              </FormBody>
              <AlertModal
                isOpen={modal}
                toggle={toggleModal}
                title={locale("alert")}
                content={modalContent}
                link={modalLink}
                className="alert-msg-container"
                InnerClassName="alert-msg"
              />
            </CreateAccountFormWrapper>
          </CreateAccountContainer>
        </RightContainer>
      </Container>
    );
  };

  return <BodyContainer>{formFields()}</BodyContainer>;
};

export default SignUp;
